// Imports
import React, { useContext } from 'react';
import styled, { css } from 'styled-components/macro';
import theme from '../../../../../shareables/theme';
import { fieldContainerBottomMargin } from './field-container';
import FieldErrorMessage from './field-error-message';
import APIConnectedFormContext from '../../../../contexts/api-connected-form';


// Initialize
const condensedBottomMarginWhenNoLabel = '0.5em';


// Define the accepted props
interface Props {
	grow?: number;
	growAll?: boolean;
	centerOnMobile?: boolean;
	moreSpaceBetween?: boolean;
}


// Styled components
const Container = styled.div<Props>`
	margin-bottom: ${fieldContainerBottomMargin};
	
	&:last-child {
		margin-bottom: 0;
	}
	
	> div {
		display: flex;
		align-items: flex-end;
		justify-content: space-between;
		
		> div[class^='field-container'] {
			margin-bottom: 0;
			
			> div[class^='field-error-message'] {
				display: none;
			}
		}
		
		@media (max-width: ${theme.breakpoints.mobile}px) {
			align-items: flex-start;
			flex-direction: column;
			
			${(props) => (props.centerOnMobile ? 'text-align: center;' : '')}
			
			> div[class^='field-container'] {
				width: 100%;
				
				&:not(:last-child) {
					margin-bottom: ${condensedBottomMarginWhenNoLabel};
				}
				
				&:not(:first-child) {
					label {
						margin-top: calc(${fieldContainerBottomMargin} - ${condensedBottomMarginWhenNoLabel});
					}
				}
				
				> div[class^='field-error-message'] {
					display: block;
				}
			}
		}
		
		@media (min-width: ${theme.breakpoints.mobile + 1}px) {
			> div[class^='field-container']:not(:last-child) {
				margin-right: ${(props) => (props.moreSpaceBetween ? '2em' : '0.5em')};
			}
			
			${(props) =>
				props.growAll
					? css`
							> div {
								flex-grow: 1;
								flex-basis: 0;
							}
						`
					: ''}
			
			${(props) =>
				props.grow
					? css`
							> div:nth-of-type(${props.grow}) {
								flex-grow: 1;
							}
						`
					: ''}
		}
	}
	
	@media (max-width: ${theme.breakpoints.mobile}px) {
		> div[class^='field-error-message'] {
			display: none;
		}
	}
`;


// Functional component
const MultiFieldContainer: React.FC<React.PropsWithChildren<Parameters<typeof Container>[0]>> = (props) => {
	// Pull out children from pass-through props
	const { children, ...passThroughProps } = props;
	
	
	// Use context
	const context = useContext(APIConnectedFormContext);
	
	
	// Determine the names of direct children fields
	const fieldNames: string[] = [];
	
	React.Children.map(children, (child) => {
		if (!React.isValidElement(child)) {
			return child;
		}
		
		const { name } = child.props as { name?: unknown };
		
		
		// @ts-ignore: displayName does exist
		if (child?.type?.displayName && name && typeof name === 'string') {
			fieldNames.push(name);
		}
	});
	
	
	// Determine what error message should be displayed, if any
	let errorMessage = '';
	
	if (context?.error.field && fieldNames.includes(context.error.field)) {
		errorMessage = context.error.message ?? '';
	}
	
	
	// Return JSX
	return (
		<Container {...passThroughProps}>
			<div>{children}</div>
			
			{errorMessage && <FieldErrorMessage message={errorMessage} />}
		</Container>
	);
};

export default MultiFieldContainer;
